/**
 * The interview's two self-sending timers.
 *
 * A single-select chip answers its question on its own, so it sends itself
 * after a beat instead of asking for a second tap. The multi-select question
 * cannot do that — the visitor may still be picking — so it waits longer, and
 * every new tick restarts the wait.
 *
 * The delays and the rule live here, apart from the component, so both can be
 * tested without a DOM.
 */

/** How long a single-select chip sits highlighted before it sends itself. */
export const SINGLE_SELECT_SEND_MS = 250;

/** How long the multi-select question waits after the last chip tapped. */
export const MULTI_SELECT_CONTINUE_MS = 2000;

/**
 * Whether a multi-select answer should start (or restart) its timer.
 *
 * Nothing selected is a real answer on the multi-select question — "none of
 * these exist" — but it is one the visitor has to mean, so it is never sent
 * for them. Typed context is the other stop: someone mid-sentence is not
 * finished, and their words would be cut off by a send.
 */
export function shouldAutoContinue(selectedCount: number, text: string): boolean {
  return selectedCount > 0 && text.trim().length === 0;
}

/** One pending self-send, replaced on every schedule and dropped on cancel. */
export interface AutoSend {
  /** Start the timer, discarding any timer already running. */
  schedule(run: () => void, delayMs: number): void;
  /** Drop the pending timer, if any. */
  cancel(): void;
  /** True while a timer is waiting to fire. */
  pending(): boolean;
}

export function createAutoSend(): AutoSend {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return {
    cancel,
    pending: () => timer !== null,
    schedule: (run, delayMs) => {
      cancel();
      timer = setTimeout(() => {
        timer = null;
        run();
      }, delayMs);
    },
  };
}
